import React, { useState, useEffect } from 'react';
import {
    Box,
    CircularProgress,
    LinearProgress,
    Typography,
    Stack,
    Paper,
    Grid,
    Chip,
    Alert
} from '@mui/material';
import { SmsService } from '../../../../service/smsService';

const POLL_INTERVAL_MS = 2000; // Poll every 2 seconds

export default function BulkSmsProgress({ batchId }) {
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [pollIntervalId, setPollIntervalId] = useState(null);

    useEffect(() => {
        if (!batchId) return;

        // Define fetchProgress inside useEffect to avoid closure issues
        const fetchProgress = async () => {
            try {
                const response = await SmsService.getBatchProgress(batchId);
                if (response?.code === 0) {
                    setProgress(response.data);
                    setError('');

                    // Stop polling if completed or failed
                    if (response.data.status === 'completed' || response.data.status === 'failed') {
                        if (pollIntervalId) clearInterval(pollIntervalId);
                    }
                } else {
                    setError(response?.message || 'Failed to fetch progress');
                }
            } catch (err) {
                setError(err.message || 'Failed to fetch progress');
                console.error('Progress fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        // Fetch immediately
        fetchProgress();

        // Set up polling
        const intervalId = setInterval(fetchProgress, POLL_INTERVAL_MS);
        setPollIntervalId(intervalId);

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [batchId, pollIntervalId]);

    if (loading && !progress) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && !progress) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!progress) {
        return <Alert severity="warning">No progress data available</Alert>;
    }

    const progressPercent = progress.totalRecipients > 0
        ? Math.round(((progress.sentCount + progress.blockedCount + progress.failedCount) / progress.totalRecipients) * 100)
        : 0;

    const isComplete = progress.status === 'completed' || progress.status === 'failed';

    return (
        <Stack spacing={3}>
            {/* Status header */}
            <Box>
                <Typography variant="subtitle2" gutterBottom>
                    Sending Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!isComplete ? (
                        <>
                            <CircularProgress size={24} />
                            <Typography variant="body2">
                                {progress.status === 'processing' ? 'Sending in progress...' : 'Pending...'}
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant="body2" color={progress.status === 'completed' ? 'success.main' : 'error.main'}>
                                {progress.status === 'completed' ? '✓ Completed' : '✗ Failed'}
                            </Typography>
                        </>
                    )}
                </Box>
            </Box>

            {/* Progress bar */}
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption">Overall Progress</Typography>
                    <Typography variant="caption">{progressPercent}%</Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                    sx={{ height: 8, borderRadius: 4 }}
                />
            </Box>

            {/* Counts grid */}
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                        <Box textAlign="center">
                            <Typography variant="h6" color="primary">
                                {progress.totalRecipients}
                            </Typography>
                            <Typography variant="caption">Total Recipients</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Box textAlign="center">
                            <Typography variant="h6" color="success.main">
                                {progress.sentCount}
                            </Typography>
                            <Typography variant="caption">Sent</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Box textAlign="center">
                            <Typography variant="h6" color="warning.main">
                                {progress.blockedCount}
                            </Typography>
                            <Typography variant="caption">Blocked (Opted-out)</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Box textAlign="center">
                            <Typography variant="h6" color="error.main">
                                {progress.failedCount}
                            </Typography>
                            <Typography variant="caption">Failed</Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Status badges */}
            {isComplete && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {progress.status === 'completed' && (
                        <Chip
                            label={`Completed at ${new Date(progress.completedAt).toLocaleTimeString()}`}
                            color="success"
                            variant="outlined"
                        />
                    )}
                    {progress.status === 'failed' && (
                        <Chip
                            label="Failed - check logs for details"
                            color="error"
                            variant="outlined"
                        />
                    )}
                </Box>
            )}

            {!isComplete && (
                <Typography variant="caption" color="text.secondary">
                    Auto-updating every {POLL_INTERVAL_MS / 1000} seconds...
                </Typography>
            )}
        </Stack>
    );
}
